import { useTranslation } from '@thoughtspot/i18n';
import cx from 'classnames';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { RD_COMPONENTS } from '../../_utils';
import { Button } from '../../button';
import { Checkbox } from '../../checkbox';
import { FormControl } from '../../form-control';
import { IconColor, IconID } from '../../icon';
import { Input } from '../../input';
import { Horizontal, Vertical } from '../../layout';
import { LoadingIndicator } from '../../loading-indicator';
import { Toggle } from '../../toggle';
import { Typography } from '../../typography';
import styles from '../select.module.scss';
import { Dropdown } from './select-dropdown';
const InternalCheckboxList = ({ isReadOnly = false, dataTestId = '', list, checkedItemList, disabledItemList = [], setCheckedItemList, separator = -1, getLabel, showSeparator = false, className = '', onChange, footerLabel = '', nullValuePlaceholder = 'NULL', onScrollPositionSave, scrollContainerRef, }) => {
    const { t } = useTranslation();
    const checkboxItemOnChange = (value) => {
        if (!setCheckedItemList)
            return;
        // Save scroll position before state change
        onScrollPositionSave === null || onScrollPositionSave === void 0 ? void 0 : onScrollPositionSave();
        const isChecked = checkedItemList.findIndex((item) => _.toLower(item) === _.toLower(value)) === -1;
        if (isChecked)
            setCheckedItemList([...checkedItemList, value]);
        else
            setCheckedItemList(_.differenceBy(checkedItemList, [value], _.toLower));
    };
    return (React.createElement("div", { ref: scrollContainerRef, className: cx(styles.internalValuesContainer, className), "data-testid": "filter-values-container" },
        React.createElement("div", { "data-testid": dataTestId, className: styles.internalCheckboxListWrapper }, list.map((value, index) => {
            const nullEmptySwitcher = () => {
                let label = value;
                if (value ===
                    t('NULL_VALUE_PLACEHOLDER_LABEL', {
                        skipInterpolation: true,
                    })) {
                    label = t('NULL_VALUE_PLACEHOLDER_LABEL', {
                        skipInterpolation: true,
                    });
                }
                else if (value === '') {
                    label = t('EMPTY_VALUE_PLACEHOLDER_LABEL', {
                        skipInterpolation: true,
                    });
                }
                else if (value === nullValuePlaceholder) {
                    label = nullValuePlaceholder;
                }
                return label;
            };
            const idValue = nullEmptySwitcher();
            const labelValue = nullEmptySwitcher();
            const isChecked = checkedItemList.findIndex((item) => _.toLower(item) === _.toLower(value)) !== -1;
            return (React.createElement("div", { "data-testid": "filter-value-item", key: idValue, className: index === separator && showSeparator
                    ? styles.internalSeparatorBox
                    : '' },
                React.createElement(Checkbox, { className: styles.internalSingleCheckboxItem, checked: isChecked, disabled: isReadOnly ||
                        disabledItemList.includes(idValue), "data-testid": isChecked
                        ? 'filter-value-checked'
                        : 'filter-value-unchecked', id: idValue, showTooltipOnEllipsis: true, tooltipContent: labelValue, label: getLabel ? getLabel(labelValue) : labelValue, onChange: (event, id) => {
                        if (onChange && id)
                            onChange(event, id);
                        if (checkboxItemOnChange && id)
                            checkboxItemOnChange(id);
                    }, highlightOnHover: true })));
        })),
        footerLabel ? (React.createElement("div", { className: styles.footerLabel }, footerLabel)) : null));
};
const InternalSelectAllCheckbox = ({ isReadOnly, label, className, isChecked, isIndeterminate, clearAll, selectAll, }) => {
    return (React.createElement(Checkbox, { className: className, id: "select-all", label: label, disabled: isReadOnly, checked: isChecked, onChange: () => (isChecked ? clearAll() : selectAll()), indeterminate: isIndeterminate, "data-testid": "select-all-checkbox" }));
};
export const MultiSelectCheckboxDropdown = ({ options = [], selectedValues = [], onChange, loading = false, searchValue = '', onSearchChange, showUnfilteredToggle = false, showAllValues = true, onShowAllValuesToggle, allowCustomValues = false, searchPlaceholder = '', footerLabel = '', nullValuePlaceholder = 'NULL', labels, placeholder = '', onMenuToggle, ...restProps }) => {
    const { t } = useTranslation();
    // Use internal checkbox components
    const CheckboxListComponent = InternalCheckboxList;
    const SelectAllCheckboxComponent = InternalSelectAllCheckbox;
    // Internal state for search and custom values
    const [internalSearchValue, setInternalSearchValue] = useState(searchValue);
    const [addCustomValueInput, setAddCustomValueInput] = useState('');
    const [customAddedValues, setCustomAddedValues] = useState([]);
    const [isAddCustomValueSectionExpanded, setIsAddCustomValueSectionExpanded,] = useState(false);
    // State for stable filterList - snapshot when dropdown opens
    const [dropdownOpenSnapshot, setDropdownOpenSnapshot] = useState(null);
    // Store scroll position to restore after re-renders
    const checkboxListRef = useRef(null);
    const scrollPositionRef = useRef(0);
    // Save scroll position before re-render
    const saveScrollPosition = () => {
        if (checkboxListRef.current) {
            scrollPositionRef.current = checkboxListRef.current.scrollTop;
        }
    };
    // Restore scroll position after re-render
    useEffect(() => {
        if (checkboxListRef.current && scrollPositionRef.current > 0) {
            checkboxListRef.current.scrollTop = scrollPositionRef.current;
        }
    });
    // Handle search value sync
    useEffect(() => {
        setInternalSearchValue(searchValue);
    }, [searchValue]);
    // Filter options based on search - use live search but stable structure
    const stableFilteredFilterValues = useMemo(() => {
        if (dropdownOpenSnapshot) {
            // Use snapshot values for structure but LIVE search for filtering
            const customAndSelectedFilterValues = _.union(customAddedValues, dropdownOpenSnapshot.selectedValues);
            const filteredCustomAndSelectedFilterValues = customAndSelectedFilterValues
                ? customAndSelectedFilterValues.filter((item) => !internalSearchValue ||
                    item
                        .toLowerCase()
                        .includes(internalSearchValue.toLowerCase()))
                : [];
            const allOptions = [...options];
            const filteredAllOptions = !internalSearchValue
                ? allOptions
                : allOptions.filter((option) => option
                    .toLowerCase()
                    .includes(internalSearchValue.toLowerCase()));
            return _.union(filteredCustomAndSelectedFilterValues, filteredAllOptions);
        }
        // Use live values when dropdown is closed
        const customAndSelectedFilterValues = _.union(customAddedValues, selectedValues);
        const filteredCustomAndSelectedFilterValues = customAndSelectedFilterValues
            ? customAndSelectedFilterValues.filter((item) => !internalSearchValue ||
                item
                    .toLowerCase()
                    .includes(internalSearchValue.toLowerCase()))
            : [];
        const allOptions = [...options];
        const filteredAllOptions = !internalSearchValue
            ? allOptions
            : allOptions.filter((option) => option
                .toLowerCase()
                .includes(internalSearchValue.toLowerCase()));
        return _.union(filteredCustomAndSelectedFilterValues, filteredAllOptions);
    }, [
        dropdownOpenSnapshot,
        options,
        customAddedValues,
        selectedValues,
        internalSearchValue,
    ]);
    // Handle search input change
    const handleSearchChange = useCallback((value) => {
        setInternalSearchValue(value);
        onSearchChange === null || onSearchChange === void 0 ? void 0 : onSearchChange(value);
    }, [onSearchChange]);
    // Handle select all
    const handleSelectAll = useCallback(() => {
        const newSelectedValues = _.union(selectedValues, stableFilteredFilterValues);
        onChange(newSelectedValues);
    }, [selectedValues, stableFilteredFilterValues, onChange]);
    // Handle clear all
    const handleClearAll = useCallback(() => {
        const newSelectedValues = _.difference(selectedValues, stableFilteredFilterValues);
        onChange(newSelectedValues);
    }, [selectedValues, stableFilteredFilterValues, onChange]);
    // Handle individual checkbox change
    const handleCheckboxChange = useCallback((checkedValues) => {
        onChange(checkedValues);
    }, [onChange]);
    // Handle add custom value
    const handleAddCustomValue = useCallback(() => {
        if (!addCustomValueInput.trim())
            return;
        const newCustomValues = [
            ...customAddedValues,
            addCustomValueInput.trim(),
        ];
        const newSelectedValues = [
            ...selectedValues,
            addCustomValueInput.trim(),
        ];
        setCustomAddedValues(newCustomValues);
        onChange(newSelectedValues);
        setAddCustomValueInput('');
        setIsAddCustomValueSectionExpanded(false);
    }, [addCustomValueInput, customAddedValues, selectedValues, onChange]);
    // Generate display for selected values in dropdown header
    // Exact alphaNumericCompareFn from original
    const alphaNumericCompareFn = (a, b) => {
        return a.localeCompare(b, 'en', { numeric: true });
    };
    const getDisplayForDropdownHead = useCallback(() => {
        const optionsLen = selectedValues.length;
        if (!optionsLen)
            return null;
        const sortedSelectedValues = selectedValues.sort(alphaNumericCompareFn);
        return (React.createElement(Horizontal, { className: styles.selectedValue, "data-tooltip-content": _.join(sortedSelectedValues, ', ') },
            React.createElement(Typography, { variant: "body-normal", ellipsis: { rows: 1 }, wrapContent: true, noMargin: true }, sortedSelectedValues[0]),
            optionsLen > 1 && (React.createElement(Typography, { variant: "body-normal", className: styles.noMarginBottom },
                "\u00A0+",
                optionsLen - 1))));
    }, [selectedValues]);
    // Extract complex expression for ESLint
    const addValueButtonText = labels === null || labels === void 0 ? void 0 : labels.addValueButtonText;
    // Generate add custom values section
    const getAddCustomValuesSection = useCallback(() => {
        if (!allowCustomValues)
            return null;
        if (!isAddCustomValueSectionExpanded) {
            return (React.createElement(Button, { type: "tertiary", size: "s", icon: IconID.PLUS, text: addValueButtonText || '', onClick: () => setIsAddCustomValueSectionExpanded(true) }));
        }
        return (React.createElement("div", { className: styles.addValueContainer },
            React.createElement(FormControl, { label: "Value", noMarginBottom: true, className: styles.addValueInput },
                React.createElement(Input.Text, { value: addCustomValueInput, onChange: (_, value) => setAddCustomValueInput(value), onEnter: (evt) => {
                        handleAddCustomValue();
                        evt.stopPropagation();
                    } })),
            React.createElement(Button, { type: "secondary", size: "xs", icon: IconID.CROSS, onClick: () => {
                    setIsAddCustomValueSectionExpanded(false);
                    setAddCustomValueInput('');
                }, className: styles.addValueBtn }),
            React.createElement(Button, { type: "primary", size: "xs", icon: IconID.CHECKMARK, onClick: handleAddCustomValue, className: styles.addValueBtn })));
    }, [
        allowCustomValues,
        isAddCustomValueSectionExpanded,
        addCustomValueInput,
        handleAddCustomValue,
        addValueButtonText,
    ]);
    // Stable computed values for dropdown content - based on INITIAL selection when dropdown opened
    const stableFilteredSelectedValues = useMemo(() => {
        if (dropdownOpenSnapshot) {
            // Use snapshot for structure - only changes with search, NOT with checkbox clicks
            return _.intersection(dropdownOpenSnapshot.selectedValues, stableFilteredFilterValues).sort(alphaNumericCompareFn);
        }
        // Use live data only when dropdown is closed (initial state)
        return _.intersection(_.union(customAddedValues, selectedValues), stableFilteredFilterValues).sort(alphaNumericCompareFn);
    }, [
        dropdownOpenSnapshot,
        stableFilteredFilterValues,
        customAddedValues,
        selectedValues,
    ]);
    const stableFilterList = useMemo(() => {
        const baseList = _.union(stableFilteredSelectedValues, stableFilteredFilterValues);
        // Only add nullValuePlaceholder if it's not empty
        if (nullValuePlaceholder && nullValuePlaceholder.trim() !== '') {
            return _.union(stableFilteredSelectedValues, [nullValuePlaceholder], stableFilteredFilterValues);
        }
        return baseList;
    }, [
        stableFilteredSelectedValues,
        nullValuePlaceholder,
        stableFilteredFilterValues,
    ]);
    // Memoize the count calculation separately to avoid re-rendering entire content
    const selectedCount = useMemo(() => {
        return _.intersection(selectedValues, stableFilteredFilterValues)
            .length;
    }, [selectedValues, stableFilteredFilterValues]);
    const selectAllLabel = useMemo(() => {
        var _a, _b;
        return (internalSearchValue === null || internalSearchValue === void 0 ? void 0 : internalSearchValue.length)
            ? ((_a = labels === null || labels === void 0 ? void 0 : labels.selectAllFromSearch) === null || _a === void 0 ? void 0 : _a.call(labels, selectedCount)) ||
                t('attributeFilter.selectAll.xSelectedFromSearch', {
                    itemNos: selectedCount,
                })
            : ((_b = labels === null || labels === void 0 ? void 0 : labels.selectAll) === null || _b === void 0 ? void 0 : _b.call(labels, selectedCount)) ||
                t('attributeFilter.selectAll.xSelected', {
                    itemNos: selectedCount,
                });
    }, [internalSearchValue, selectedCount, labels, t]);
    // Generate dropdown content without useMemo to avoid ESLint parser bug
    const getDropdownContent = () => {
        // Use stable filter list that doesn't change during dropdown interaction
        const filterList = stableFilterList;
        return (React.createElement("div", { className: styles.multiSelectCheckboxDropdown },
            React.createElement(Vertical, { spacing: "b" },
                React.createElement("div", null,
                    React.createElement(Input.Text, { placeholder: searchPlaceholder, value: internalSearchValue, prefixIcon: IconID.MAGNIFYING_GLASS, onChange: (_, value) => handleSearchChange(value), className: styles.searchInputSection, inputClassName: styles.searchInput, autoFocus: true }),
                    React.createElement(Horizontal, { className: styles.allCheckboxContainer },
                        React.createElement(SelectAllCheckboxComponent, { isReadOnly: loading, label: selectAllLabel, className: "", isChecked: selectedCount ===
                                stableFilteredFilterValues.length &&
                                stableFilteredFilterValues.length !== 0, isIndeterminate: selectedCount !== 0 &&
                                selectedCount !==
                                    stableFilteredFilterValues.length, clearAll: handleClearAll, selectAll: handleSelectAll })),
                    loading ? (React.createElement(LoadingIndicator.Global, { className: styles.loadingIndicator },
                        React.createElement("div", null))) : (React.createElement(CheckboxListComponent, { list: filterList, checkedItemList: selectedValues, setCheckedItemList: handleCheckboxChange, separator: stableFilteredSelectedValues.length - 1, showSeparator: true, className: styles.valueList, footerLabel: footerLabel, dataTestId: "multi-select-checkbox-list", isReadOnly: loading, nullValuePlaceholder: nullValuePlaceholder, onScrollPositionSave: saveScrollPosition, scrollContainerRef: checkboxListRef })),
                    showUnfilteredToggle && (React.createElement(Horizontal, { className: styles.toggleAndIconContainer },
                        React.createElement(Toggle, { id: "show-all-values", label: t('filters.Show_Unfiltered_Values'), checked: showAllValues, labelPosition: "right", onChange: () => onShowAllValuesToggle === null || onShowAllValuesToggle === void 0 ? void 0 : onShowAllValuesToggle(!showAllValues), disabled: loading, className: "showAllValuesTogglerCheckbox" }),
                        React.createElement(Button, { icon: IconID.INFORMATION_CIRCLE, iconColor: IconColor.GRAY, "data-testid": "unfiltered-toggle-tooltip", "data-tooltip-content": t('attributeFilter.showUnfilteredValues.tooltip'), ariaLabel: t('attributeFilter.showUnfilteredValues.tooltip'), type: "tertiary", size: "s" }))),
                    allowCustomValues && (React.createElement(Horizontal, { className: styles.addValuesSection }, getAddCustomValuesSection()))))));
    };
    // Handle menu toggle
    const handleMenuToggle = useCallback((visible) => {
        if (visible) {
            // Snapshot current state when dropdown opens
            setDropdownOpenSnapshot({
                selectedValues: [...selectedValues],
                searchValue: internalSearchValue,
            });
            // Reset scroll to top when dropdown opens
            if (checkboxListRef.current) {
                checkboxListRef.current.scrollTop = 0;
            }
            scrollPositionRef.current = 0;
        }
        else {
            // Clear snapshot and reset state when dropdown closes
            setDropdownOpenSnapshot(null);
            setIsAddCustomValueSectionExpanded(false);
            setAddCustomValueInput('');
            setCustomAddedValues([]);
            // Always clear search on dropdown close (like original)
            setInternalSearchValue('');
            // Notify parent of search clear if callback provided
            onSearchChange === null || onSearchChange === void 0 ? void 0 : onSearchChange('');
        }
        onMenuToggle === null || onMenuToggle === void 0 ? void 0 : onMenuToggle(visible);
    }, [selectedValues, internalSearchValue, onMenuToggle, onSearchChange]);
    return (React.createElement(Dropdown, { ...restProps, placeholder: placeholder, optionsGroups: [], customDropdownContent: getDropdownContent(), customCurrentSelectionDisplay: getDisplayForDropdownHead(), onMenuToggle: handleMenuToggle, enableScroll: false, disabled: loading }));
};
MultiSelectCheckboxDropdown.RD_COMPONENT =
    RD_COMPONENTS.MULTI_SELECT_CHECKBOX_DROPDOWN;
//# sourceMappingURL=multi-select-checkbox-dropdown.js.map